/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
} from "@chakra-ui/modal";
import Card from "components/card";
import DynamicInputField from "components/fields/DynamicInputField";
import { Select, Button } from '@chakra-ui/react';

const CreateReceiverAccountModal: React.FC<{
    setSwalProps: any;
    receiverId: string;
    isOpen: boolean;
    onClose(): void;
    triggerReloadReceiverAccountList(): void;
}> = ({ setSwalProps, isOpen, onClose, receiverId, triggerReloadReceiverAccountList }) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [methodInfoList, setMethodInfoList] = useState<any>([]);
    const [assetList, setAssetList] = useState<any>([]);
    const [asset, setAsset] = useState<string>("");
    const [method, setMethod] = useState<string>("");
    const [filteredMethodList, setFilteredMethodList] = useState<any>([]);
    const [filteredMethodFields, setFilteredMethodFields] = useState<any>([]);
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<any>("");
    const createFormRef = useRef(null);
    const subaccountId = localStorage.getItem("subaccountId");

    const fetchAccountDetail = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/subaccount/get-subaccount-by-id?subaccountid=${subaccountId}`
            );

            const accountData = response.data.subaccount?.payload;

            if (!accountData) {
                throw new Error('No subaccount data found');
            }

            const assetMap = new Map();

            accountData.account_methods.forEach((method: any) => {
                if (!assetMap.has(method.asset)) {
                    assetMap.set(method.asset, {
                        asset: method.asset,
                        decimal: method.decimal ?? 2,
                        crypto: method.crypto
                    });
                }
            });

            const uniqueAssets = Array.from(assetMap.values());
            setAssetList(uniqueAssets);
            setMethodInfoList(accountData.account_methods);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            console.error("Error fetching subaccount details:", error);
        }
    };

    const closeModal = async () => {
        onClose();
        setErrorMessage("");
        setAsset("");
        setMethod("");
        setFilteredMethodList([]);
        setFilteredMethodFields([]);
    };

    const submitHandler = async (e: any) => {
        setErrorMessage("");

        e.preventDefault();

        const formData = new FormData(createFormRef.current);
        const formObj = Object.fromEntries(formData.entries());

        formObj.receiver_id = receiverId;
        formObj.asset = asset;
        formObj.method = method;

        try {
            setIsCreating(true);

            formObj.username = localStorage.getItem("username");
            formObj.userRole = localStorage.getItem("userRole")?.toLowerCase();
            formObj.subaccount_id = localStorage.getItem("subaccountId");

            await axios.post(
                process.env.REACT_APP_API_URL + `/api/${localStorage.getItem("userRole")?.toLowerCase()}/receiveraccount/action/receiver-account-create-new`,
                formObj
            )
                .then(function (response) {
                    if (response && response.data?.status === 'ok') {
                        closeModal();
                        setSwalProps({
                            show: true,
                            icon: "success",
                            title: "Success",
                            html: "Add receiver account successfully",
                            showConfirmButton: true,
                            didClose: () => {
                                setSwalProps({});
                                setIsCreating(false);
                                triggerReloadReceiverAccountList();
                            },
                        });
                    } else {
                        setIsCreating(false);
                        setErrorMessage('Add receiver account has been failed');
                    }
                })
                .catch(function (error) {
                    setIsCreating(false);
                    if (error.response) {
                        setErrorMessage(error.response.data.message);
                    } else {
                        setErrorMessage('Add receiver account has been failed');
                    }
                });
        } catch (error: any) {
            setIsCreating(false);
            setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: "Something went wrong, please try again later.",
                showConfirmButton: true,
                didClose: () => {
                    setSwalProps({});
                    setIsCreating(false);
                },
            });
        }
    };

    const handleAssetDropdownChange = async (event: any) => {
        setErrorMessage("");
        setFilteredMethodList([]);
        setFilteredMethodFields([]);
        setMethod("");

        const { value } = event.target;
        setAsset(value);
        if (value !== "") {
            setIsLoading(true);
            const getListResult = await methodInfoList.filter((x: any) => x.asset === value);
            setFilteredMethodList(getListResult);
            setIsLoading(false);
        }
    };

    const handleMethodDropdownChange = async (event: any) => {
        setErrorMessage("");
        const { value } = event.target;
        if (value !== "") {
            setMethod(value);
            const getInfo = methodInfoList.find((x: any) => x.asset === asset && x.method_name === value);
            setFilteredMethodFields(getInfo.fields);
        }
    };

    const fieldsRendering = (fields: any) => {
        const renderInput = (field: any) => {
            const capitalizeLabel = (label: any) => {
                let modifiedLabel = label.split('_')
                    .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');

                switch (label) {
                    case "bsb":
                        modifiedLabel = "BSB";
                        break;
                    case "bic":
                        modifiedLabel = "BIC";
                        break;
                    case "iban":
                        modifiedLabel = "IBAN";
                        break;
                }

                return modifiedLabel;
            };

            let fieldType = "text";
            switch (field.type) {
                case "number":
                    fieldType = "number";
                    break;
                case "string":
                default:
                    fieldType = "text";
                    break;
            }

            let dropdownOption: string[] = [];
            if (field.enum_values) {
                fieldType = "select"
                dropdownOption = field.enum_values.split("|");
            }

            return (
                <>
                    {fieldType !== "select" && (
                        <DynamicInputField
                            key={field.name}
                            minLength={field.length?.min ?? undefined}
                            maxLength={field.length?.max ?? undefined}
                            disabled={isCreating || isLoading}
                            variant="auth"
                            extra="mb-3 shadow-sm"
                            label={capitalizeLabel(field.name)}
                            id={field.name}
                            name={field.name}
                            type={fieldType}
                            required={field.required}
                            autoComplete="off"
                        />
                    )}
                    {fieldType === "select" && (
                        <>
                            <label className="mb-1 text-sm text-navy-700 dark:text-white font-medium">
                                {capitalizeLabel(field.name)}{(field.required ? <span className="text-red-500">*</span> : "")}
                            </label>
                            <Select
                                required={field.required}
                                disabled={isCreating || isLoading}
                                id={field.name}
                                name={field.name}
                                className="h-10 rounded-md custom-select relative bg-white border border-gray-300 focus:outline-none py-2 pl-3 pr-10 sm:text-sm w-full mb-3 shadow-sm"
                            >
                                <option value="">-- Please Select --</option>
                                {dropdownOption?.length > 0 &&
                                    dropdownOption.map((option: any) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                            </Select>
                        </>
                    )}
                </>
            );
        };

        return fields.map(renderInput);
    };

    useEffect(() => {
        const fetchData = async () => {
            await fetchAccountDetail();
        };

        if (isOpen && assetList.length === 0) {
            fetchData();
        }
    }, [isOpen]); // eslint-disable-line

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
                <ModalOverlay className="bg-[#000] !opacity-30" />
                <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
                    <ModalBody className="p-6 md:p-7">
                        <div className="mb-4 flex items-start gap-3">
                            <div className="rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-400 dark:ring-primary-900/30">
                                <svg
                                    viewBox="0 0 24 24"
                                    className="h-5 w-5"
                                    fill="currentColor"
                                >
                                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Add New Account
                                </h1>
                                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                    Create a new receiver account for payments
                                </p>
                            </div>
                        </div>

                        <form onSubmit={submitHandler} ref={createFormRef}>
                            <div className="mb-5">
                                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                                    Asset <span className="text-red-500">*</span>
                                </label>

                                <Select 
                                    required={true} 
                                    disabled={isCreating || isLoading} 
                                    id="asset" 
                                    value={asset} 
                                    onChange={handleAssetDropdownChange} 
                                    className="dark:bg-neutral-900 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none ring-primary-500/0 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:text-white"
                                >
                                    <option value="">{isLoading ? "Retrieving Available..." : "-- Please Select --"}</option>
                                    {assetList && assetList.filter((item: any) => !item.crypto).length > 0 &&
                                        <optgroup label="Fiat">
                                            {assetList && assetList.length > 0 && assetList.filter((item: any) => !item.crypto).map((item: any) => (
                                                <option key={item.asset} value={item.asset} data-decimal={item.decimal}>
                                                    {item.asset}
                                                </option>
                                            ))}
                                        </optgroup>
                                    }
                                    {assetList && assetList.filter((item: any) => item.crypto).length > 0 &&
                                        <optgroup label="Crypto">
                                            {assetList && assetList.length > 0 && assetList.filter((item: any) => item.crypto).map((item: any) => (
                                                <option key={item.asset} value={item.asset} data-decimal={item.decimal}>
                                                    {item.asset}
                                                </option>
                                            ))}
                                        </optgroup>
                                    }
                                </Select>
                            </div>

                            <div className="mb-5">
                                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                                    Method <span className="text-red-500">*</span>
                                </label>

                                <Select 
                                    required={true} 
                                    disabled={isCreating || isLoading} 
                                    id="method" 
                                    value={method} 
                                    onChange={handleMethodDropdownChange} 
                                    className="dark:bg-neutral-900 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none ring-primary-500/0 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:text-white"
                                >
                                    <option value="">-- Please Select --</option>
                                    {filteredMethodList && filteredMethodList.length > 0 && filteredMethodList.map((type: any) => (
                                        <option key={type.method_name} value={type.method_name}>{type.method_name}</option>
                                    ))}
                                </Select>
                            </div>

                            {filteredMethodFields && fieldsRendering(filteredMethodFields)}

                            {errorMessage && (
                                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
                                    {errorMessage}
                                </div>
                            )}

                            <div className="flex justify-end gap-2">
                                <Button
                                    onClick={() => closeModal()}
                                    isDisabled={isCreating}
                                    className="dark:bg-neutral-900 dark:hover:bg-neutral-800 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-white/10 dark:text-white"
                                >
                                    Cancel
                                </Button>

                                <Button
                                    type="submit"
                                    isDisabled={isCreating}
                                    isLoading={isCreating}
                                    loadingText='Adding Account...'
                                    className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 disabled:cursor-not-allowed disabled:bg-gray-400"
                                >
                                    Add Account
                                </Button>
                            </div>
                        </form>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
};

export default CreateReceiverAccountModal;